<template>
  <div class="page-view">

    <!-- Header -->
    <BasePageHeader :title="bundle?.name ?? $t('entity.bundle')" @back="$router.back()">
      <template #actions>
        <span
          class="badge badge-pill"
          :class="{
            'badge-success': allVerified,
            'badge-warning': isPartial && !allVerified,
            'badge-muted':   !allVerified && !isPartial,
          }"
        >{{ statusLabel }}</span>
      </template>
    </BasePageHeader>

    <EmptyState v-if="loading" :message="$t('common.loading')" />
    <EmptyState v-else-if="loadError" :message="loadError" variant="error" />

    <template v-else>

      <!-- Progress bar -->
      <div class="flex items-center gap-3.5 px-4 pt-3.5 pb-2">
        <div class="flex-1 h-2 bg-[#1f2937] rounded overflow-hidden">
          <div
            class="h-full rounded transition-[width] duration-300"
            :class="{
              'bg-green-700': allVerified,
              'bg-yellow-600': isPartial && !allVerified,
              'bg-blue-500': !allVerified && !isPartial,
            }"
            :style="{ width: progressPercent + '%' }"
          />
        </div>
        <div class="text-sm text-[var(--color-muted)] shrink-0">{{ verifiedCount }} / {{ items.length }} {{ $t('bundles.verification').toLowerCase() }}</div>
      </div>

      <!-- Mode selector -->
      <div class="flex gap-2 px-4 pb-3.5">
        <button
          class="btn flex-1"
          :class="mode === 'trust' ? 'primary' : 'ghost'"
          @click="mode = 'trust'"
        >{{ $t('bundles.highTrust') }}</button>
        <button
          class="btn flex-1"
          :class="mode === 'verify' ? 'primary' : 'ghost'"
          @click="mode = 'verify'"
        >{{ $t('bundles.verification') }}</button>
      </div>

      <!-- HIGH-TRUST MODE -->
      <div v-if="mode === 'trust'" class="px-4">
        <div class="text-[.9rem] text-gray-400 mb-4 leading-relaxed">
          {{ $t('bundles.highTrustDesc', { count: items.length }) }}
        </div>
        <button class="btn primary w-full" :disabled="submitting || allVerified" @click="verifyWhole">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          {{ allVerified ? $t('bundles.verifyWholeDone') : $t('bundles.verifyWhole') }}
        </button>
      </div>

      <!-- VERIFICATION MODE -->
      <div v-else>
        <!-- Scan bar -->
        <div class="px-4 pb-3">
          <SearchField
            ref="scanner"
            :scan-mode="true"
            :error="scanError"
            :loading="scanLoading"
            :placeholder="$t('bundles.scanPlaceholder')"
            @scan="onScan"
          />
        </div>

        <!-- Checklist -->
        <div class="flex flex-col gap-1.5 px-4">
          <div
            v-for="bItem in items"
            :key="bItem.id"
            class="data-card flex items-center gap-3 p-3"
            :class="{
              'border-green-700 opacity-65': bItem.verified,
              'border-red-500 opacity-65':  bItem.missing,
            }"
          >
            <div
              class="w-[26px] h-[26px] rounded-full border-2 border-gray-600 flex items-center justify-center shrink-0"
              :class="{
                'bg-green-700 border-green-700': bItem.verified,
                'bg-red-950 border-red-500': bItem.missing,
              }"
            >
              <svg v-if="bItem.verified" class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              <svg v-else-if="bItem.missing" class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </div>
            <div class="data-card__main">
              <div class="data-card__name">{{ bItem.item?.name ?? `#${bItem.id}` }}</div>
              <div class="flex gap-1.5 flex-wrap mt-0.5">
                <span v-if="bItem.serial_number" class="badge badge-muted">SN: {{ bItem.serial_number }}</span>
                <span v-if="bItem.asset_tag"     class="badge badge-muted">{{ $t('ui.assetTagLabel') }} {{ bItem.asset_tag }}</span>
              </div>
            </div>
            <!-- Mark as missing -->
            <button
              v-if="!bItem.verified && !bItem.missing"
              class="btn-icon danger"
              @click="markItemMissing(bItem)"
              :title="$t('bundles.markAsMissing')"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Partial bundle actions -->
      <div v-if="isPartial && !allVerified" class="mx-4 mt-3.5 bg-[#1c1207] border border-[#92400e] rounded-xl p-4">
        <div class="text-[.9rem] text-yellow-100 mb-3.5">
          {{ $t('bundles.partialMsg', { status: $t('bundles.status.partial').toLowerCase(), count: missingCount }) }}
        </div>
        <div class="flex gap-2.5">
          <button class="btn danger flex-1" @click="shipAnyway" :disabled="submitting">{{ $t('bundles.shipAnyway') }}</button>
          <button class="btn primary flex-1" @click="findSubstitute">{{ $t('bundles.findSubstitute') }}</button>
        </div>
      </div>

      <div v-if="substituteRows" class="data-card mx-4 mt-3 flex-col items-stretch" role="region" :aria-label="$t('bundles.findSubstitute')">
        <div class="flex justify-between">
          <strong>{{ $t('bundles.findSubstitute') }}</strong>
          <button class="btn ghost small" @click="substituteRows = null">{{ $t('common.close') }}</button>
        </div>
        <EmptyState v-if="substituteLoading" :message="$t('common.loading')" />
        <div v-for="row in substituteRows" :key="row.member.bundle_item_id" class="form-field">
          <div class="form-label">{{ row.member.item?.name }}</div>
          <EmptyState v-if="!row.candidates.length" :message="$t('bundles.noSubstitutes')" />
          <button v-for="candidate in row.candidates" :key="candidate.id" class="btn ghost"
            :disabled="submitting" @click="selectSubstitute(row.member, candidate)">
            {{ candidate.assetTag || candidate.serial_number || '#' + candidate.id }}
          </button>
        </div>
      </div>

      <!-- All done CTA -->
      <div v-if="allVerified" class="flex flex-col items-center gap-2 py-6 px-4 text-center text-green-400 font-semibold text-[.95rem]">
        <svg viewBox="0 0 24 24" fill="none" stroke="#4ade80" stroke-width="2.5" class="w-10 h-10">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
        {{ $t('bundles.allVerifiedMsg') }}
      </div>

    </template>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from '@/composables/useToast'
import SearchField from '@/components/search/SearchField.vue'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import EmptyState from '@/components/base/EmptyState.vue'
import { getBundleDetail, verifyBundle, verifyBundleItem, updateBundleVerification, getItemInstances } from '@/api/api.js'

const route  = useRoute()
const router = useRouter()
const toast  = useToast()
const { t }  = useI18n()

const projectId = computed(() => route.params.projectId)
const bundleId  = computed(() => route.params.bundleId)

// ── Data ─────────────────────────────────────────────────────────────────────
const bundle    = ref(null)
const items     = ref([])
const loading   = ref(false)
const loadError = ref(null)
const submitting = ref(false)

async function loadBundle() {
  loading.value  = true
  loadError.value = null
  try {
    const res = await getBundleDetail(projectId.value, bundleId.value)
    bundle.value = res.bundle ?? null
    items.value  = (res.items ?? []).map(i => ({ ...i, verified: !!i.verified, missing: !!i.missing }))
  } catch (e) {
    loadError.value = t('bundles.loadBundleFailed')
  } finally {
    loading.value = false
  }
}

onMounted(loadBundle)

// ── Status ────────────────────────────────────────────────────────────────────
const mode = ref('trust')   // 'trust' | 'verify'

const verifiedCount = computed(() => items.value.filter(i => i.verified).length)
const missingCount  = computed(() => items.value.filter(i => !i.verified).length)
const allVerified   = computed(() => items.value.length > 0 && items.value.every(i => i.verified))
const isPartial     = computed(() => items.value.some(i => i.verified || i.missing) && !allVerified.value)

const progressPercent = computed(() =>
  items.value.length ? Math.round((verifiedCount.value / items.value.length) * 100) : 0
)

const progressFillClass = computed(() => ({
  'bundle-progress__fill--complete': allVerified.value,
  'bundle-progress__fill--partial':  isPartial.value,
}))

const statusLabel = computed(() => {
  if (allVerified.value) return t('bundles.status.complete')
  if (isPartial.value)   return t('bundles.status.partial')
  return t('bundles.status.unverified')
})

const statusChipClass = computed(() => ({
  'bundle-status-chip--complete': allVerified.value,
  'bundle-status-chip--partial':  isPartial.value,
  'bundle-status-chip--unverified': !allVerified.value && !isPartial.value,
}))

// ── High-Trust ────────────────────────────────────────────────────────────────
async function verifyWhole() {
  submitting.value = true
  try {
    const result = await verifyBundle(projectId.value, bundleId.value, { mode: 'full' })
    items.value = result.items
    toast.success(t('bundles.bundleVerified'))
  } catch (e) {
    toast.error(t('bundles.verificationFailed'))
  } finally {
    submitting.value = false
  }
}

// ── Verification scan ─────────────────────────────────────────────────────────
const scanError   = ref(null)
const scanLoading = ref(false)
const scanner     = ref(null)

async function onScan(barcode) {
  scanError.value   = null
  scanLoading.value = true

  try {
    const match = items.value.find(i =>
      String(i.id) === barcode ||
      i.serial_number?.toLowerCase() === barcode.toLowerCase() ||
      i.asset_tag?.toLowerCase()     === barcode.toLowerCase()
    )

    if (!match) {
      scanError.value = t('bundles.notInBundle', { barcode })
      return
    }

    if (match.verified) {
      scanError.value = t('bundles.alreadyVerified', { name: match.item?.name ?? barcode })
      return
    }

    const result = await verifyBundleItem(projectId.value, bundleId.value, { item_metadata_id: match.id })
    items.value = result.items
    toast.success(t('bundles.verifiedItem', { name: match.item?.name ?? barcode }))
  } catch (e) {
    scanError.value = e?.data?.error ?? t('bundles.scanFailed')
  } finally {
    scanLoading.value = false
    scanner.value?.focus()
  }
}

async function markItemMissing(bItem) {
  try {
    const result = await updateBundleVerification(projectId.value, bundleId.value,
      { mode: 'missing', bundle_item_id: bItem.bundle_item_id });
    items.value = result.items;
    toast.warning(t('bundles.markedMissing', { name: bItem.item?.name ?? '#' + bItem.id }));
  } catch { toast.error(t('bundles.verificationFailed')); }
}

// ── Partial actions ───────────────────────────────────────────────────────────
async function shipAnyway() {
  submitting.value = true
  try {
    const result = await verifyBundle(projectId.value, bundleId.value, { mode: 'partial' })
    items.value = result.items
    toast.warning(t('bundles.shippedPartial'))
    router.back()
  } catch (e) {
    toast.error(t('bundles.shipFailed'))
  } finally {
    submitting.value = false
  }
}

const substituteRows = ref(null);
const substituteLoading = ref(false);
async function findSubstitute() {
  substituteLoading.value = true;
  substituteRows.value = [];
  try {
    const rows = [];
    for (const member of items.value.filter(i => !i.verified)) {
      const response = await getItemInstances(member.item_type_id);
      const candidates = (response.results ?? response ?? []).filter(i =>
        i.id !== member.id && i.available && !i.is_checked_out && !i.in_downtime);
      rows.push({ member, candidates });
    }
    substituteRows.value = rows;
  } catch { toast.error(t('bundles.scanFailed')); }
  finally { substituteLoading.value = false; }
}
async function selectSubstitute(member, candidate) {
  submitting.value = true;
  try {
    const result = await updateBundleVerification(projectId.value, bundleId.value,
      { mode: 'substitute', bundle_item_id: member.bundle_item_id, item_metadata_id: candidate.id });
    items.value = result.items;
    substituteRows.value = null;
  } catch (e) { toast.error(e?.data?.error ?? t('bundles.verificationFailed')); }
  finally { submitting.value = false; }
}

</script>


